import { Injectable, Inject, InjectionToken } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NetworkService } from '@angular-workspace/core';

/**
 * Injection token for file API base URL
 */
export const FILE_BASE_URL = new InjectionToken<string>('FILE_BASE_URL');

/**
 * Injection token for file API path
 */
export const FILE_API_PATH = new InjectionToken<string>('FILE_API_PATH');

/**
 * File API Service for handling file-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class FileApiService {
  constructor(
    private networkService: NetworkService,
    private http: HttpClient,
    @Inject(FILE_BASE_URL) private fileBaseUrl: string,
    @Inject(FILE_API_PATH) private fileApiPath: string,
  ) {}

  /**
   * Upload an asset file
   * @param file The file to upload
   * @returns Promise with upload response including status code
   */
  async assetUpload(file: File): Promise<{ data: any; status: number }> {
    try {
      // Construct the full URL for asset upload
      // URL: https://v2.dev.file.iudx.io/v1/assets
      const url = `${this.fileBaseUrl}v1/assets`;

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      // Use HttpClient directly with observe: 'response' to get status code
      // The browser will automatically set Content-Type with boundary for multipart/form-data
      return new Promise((resolve, reject) => {
        this.http.post(url, formData, {
          headers: new HttpHeaders({}), // Empty headers - browser sets Content-Type automatically
          observe: 'response' as const,
        }).subscribe({
          next: (response) => {
            resolve({
              data: response.body,
              status: response.status,
            });
          },
          error: (error) => {
            console.error('Error uploading asset:', error);
            reject(error);
          },
        });
      });
    } catch (error) {
      console.error('Error uploading asset:', error);
      throw error;
    }
  }

  /**
   * Initiate multipart upload to databank
   * @param databankId The databank ID
   * @param key The file key/name
   * @param contentType The file content type
   * @param numParts Number of parts for multipart upload
   * @returns Promise with upload initiation response
   */
  async initiateUpload(
    databankId: string,
    key: string,
    contentType: string,
    numParts: number
  ): Promise<{ data: any; status: number }> {
    try {
      // Construct the full URL for databank upload
      // URL: https://v2.dev.file-s3.iudx.io/v1/databanks/{databankId}/uploads
      const url = `${this.fileBaseUrl}v1/databanks/${databankId}/uploads`;

      const body = {
        key,
        contentType,
        numParts,
      };

      console.log('Initiating upload:', { url, body });

      // Use HttpClient directly with observe: 'response' to get status code
      return new Promise((resolve, reject) => {
        this.http
          .post(url, body, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
            observe: 'response' as const,
          })
          .subscribe({
            next: (response) => {
              resolve({
                data: response.body,
                status: response.status,
              });
            },
            error: (error) => {
              console.error('Error initiating upload:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error initiating upload:', error);
      throw error;
    }
  }

  /**
   * Upload file part to presigned URL
   * @param presignedUrl The presigned URL for upload
   * @param fileData The file data to upload
   * @param options Optional headers (e.g. Content-Type - use application/octet-stream to match challenge uploads)
   * @returns Promise with upload response
   */
  async uploadPart(presignedUrl: string, fileData: Blob, options?: { headers?: Record<string, string> }): Promise<any> {
    try {
      const headers = options?.headers ? new HttpHeaders(options.headers) : new HttpHeaders({});
      return new Promise((resolve, reject) => {
        this.http
          .put(presignedUrl, fileData, {
            headers,
            observe: 'response' as const,
            reportProgress: true,
          })
          .subscribe({
            next: (response) => {
              resolve(response);
            },
            error: (error) => {
              console.error('Error uploading part:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error uploading part:', error);
      throw error;
    }
  }

  /**
   * Complete multipart upload
   * @param databankId The databank ID
   * @param uploadId The upload ID from initiation
   * @param key The file key/name
   * @param parts Array of uploaded parts with ETag
   * @returns Promise with completion response
   */
  async completeUpload(
    databankId: string,
    uploadId: string,
    key: string,
    parts: Array<{ partNumber: number; eTag: string }>
  ): Promise<{ data: any; status: number }> {
    try {
      const url = `${this.fileBaseUrl}v1/databanks/${databankId}/uploads/${uploadId}`;

      const body = {
        key,
        parts,
      };

      console.log('Completing upload:', { url, body });

      return new Promise((resolve, reject) => {
        this.http
          .put(url, body, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
            observe: 'response' as const,
          })
          .subscribe({
            next: (response) => {
              resolve({
                data: response.body,
                status: response.status,
              });
            },
            error: (error) => {
              console.error('Error completing upload:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error completing upload:', error);
      throw error;
    }
  }

  /**
   * List files in databank
   * @param databankId The databank ID
   * @param maxKeys Maximum number of files to return
   * @param recursive Whether to list files recursively
   * @returns Promise with file list response
   */
  async listFiles(
    databankId: string,
    maxKeys: number = 1000,
    recursive: boolean = true
  ): Promise<{ data: any; status: number }> {
    try {
      const url = `${this.fileBaseUrl}v1/databanks/${databankId}/files`;

      const body = {
        maxKeys,
        recursive,
      };

      console.log('Listing files:', { url, body });

      return new Promise((resolve, reject) => {
        this.http
          .post(url, body, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
            observe: 'response' as const,
          })
          .subscribe({
            next: (response) => {
              resolve({
                data: response.body,
                status: response.status,
              });
            },
            error: (error) => {
              console.error('Error listing files:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error listing files:', error);
      throw error;
    }
  }

  /**
   * Get file metadata (POST v1/databanks/{databankId}/files/metadata).
   * Payload is passed from the component (e.g. { key: string }).
   * @param databankId The databank/asset ID
   * @param payload Request body (e.g. { key: string })
   * @returns Promise with metadata response
   */
  async getFileMetadata(
    databankId: string,
    payload: Record<string, unknown>
  ): Promise<{ data: any; status: number }> {
    try {
      const url = `${this.fileBaseUrl}v1/databanks/${databankId}/files/metadata`;

      return new Promise((resolve, reject) => {
        this.http
          .post(url, payload, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
            observe: 'response' as const,
          })
          .subscribe({
            next: (response) => {
              resolve({
                data: response.body,
                status: response.status,
              });
            },
            error: (error) => {
              console.error('Error fetching file metadata:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error fetching file metadata:', error);
      throw error;
    }
  }

  /**
   * Generate file preview (POST v1/databanks/{databankId}/files/preview).
   * Call after the files API returns 200 OK; payload is { key: "name of the file" }.
   * @param databankId The databank/asset ID (e.g. dataset ID)
   * @param key The file key/name (e.g. "eco.pdf")
   * @returns Promise with preview response
   */
  async generatePreview(
    databankId: string,
    key: string
  ): Promise<{ data: any; status: number }> {
    try {
      const url = `${this.fileBaseUrl}v1/databanks/${databankId}/files/preview`;

      const body = { key };

      console.log('Generating preview:', { url, body });

      return new Promise((resolve, reject) => {
        this.http
          .post(url, body, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
            observe: 'response' as const,
          })
          .subscribe({
            next: (response) => {
              resolve({
                data: response.body,
                status: response.status,
              });
            },
            error: (error) => {
              console.error('Error generating preview:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error generating preview:', error);
      throw error;
    }
  }

  /**
   * Get files for preview
   * @param assetId The asset ID
   * @returns Promise with files response
   */
  async getFilesForPreview(
    assetId: string,
    payload?: {
      prefix?: string;
      delimiter?: string;
      maxKeys?: number;
      recursive?: boolean;
    }
  ): Promise<{ data: any; status: number }> {
    try {
      const url = `${this.fileBaseUrl}v1/databanks/${assetId}/files`;

      const body = payload ?? {};

      console.log('Getting files for preview:', { url, body });

      return new Promise((resolve, reject) => {
        this.http
          .post(url, body, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
            observe: 'response' as const,
          })
          .subscribe({
            next: (response) => {
              resolve({
                data: response.body,
                status: response.status,
              });
            },
            error: (error) => {
              console.error('Error getting files for preview:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error getting files for preview:', error);
      throw error;
    }
  }

  /**
   * Get presigned download URL for a file.
   * Use when payload.presigned is true - API returns JSON with presignedUrl.
   * @param databankId The databank/asset ID
   * @param payload Request body: { key: string, presigned: true }
   * @returns Promise with { presignedUrl: string, expiresAt: string }
   */
  async getPresignedDownloadUrl(
    databankId: string,
    payload: { key: string; presigned: true }
  ): Promise<{ presignedUrl: string; expiresAt: string }> {
    try {
      const url = `${this.fileBaseUrl}v1/databanks/${databankId}/files/download`;

      return new Promise((resolve, reject) => {
        this.http
          .post<{ success: boolean; data: { presignedUrl: string; expiresAt: string } }>(
            url,
            payload,
            {
              headers: new HttpHeaders({
                'Content-Type': 'application/json',
              }),
              observe: 'response' as const,
            }
          )
          .subscribe({
            next: (response) => {
              const body = response.body;
              if (body?.success && body?.data?.presignedUrl) {
                resolve({
                  presignedUrl: body.data.presignedUrl,
                  expiresAt: body.data.expiresAt ?? '',
                });
              } else {
                reject(new Error('No presigned URL in response'));
              }
            },
            error: (error) => {
              console.error('Error fetching presigned URL:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error fetching presigned URL:', error);
      throw error;
    }
  }

  /**
   * Get a single file as Blob for viewing (e.g. when presigned is false).
   * @param databankId The databank/asset ID
   * @param payload Request body: { key: string, presigned?: boolean }
   * @returns Promise with Blob
   */
  async getFileBlob(
    databankId: string,
    payload: { key: string; presigned?: boolean }
  ): Promise<Blob> {
    try {
      const url = `${this.fileBaseUrl}v1/databanks/${databankId}/files/download`;

      return new Promise((resolve, reject) => {
        this.http
          .post(url, payload, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
            responseType: 'blob',
            observe: 'response' as const,
          })
          .subscribe({
            next: (response) => {
              if (response.body) {
                resolve(response.body);
              } else {
                reject(new Error('No data received'));
              }
            },
            error: (error) => {
              console.error('Error fetching file blob:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error fetching file blob:', error);
      throw error;
    }
  }

  /**
   * Get presigned download URL for the full databank (ZIP).
   * API returns JSON with downloadUrl and expiresAt; use the URL to trigger download.
   * @param databankId The databank ID to download
   * @returns Promise with { downloadUrl: string, expiresAt: string }
   */
  async getDatabankDownloadUrl(databankId: string): Promise<{ downloadUrl: string; expiresAt: string }> {
    try {
      const url = `${this.fileBaseUrl}v1/databanks/${databankId}/download`;

      return new Promise((resolve, reject) => {
        this.http
          .get<{ success: boolean; data: { downloadUrl: string; expiresAt: string } }>(url, {
            observe: 'response' as const,
          })
          .subscribe({
            next: (response) => {
              const body = response.body;
              if (body?.success && body?.data?.downloadUrl) {
                resolve({
                  downloadUrl: body.data.downloadUrl,
                  expiresAt: body.data.expiresAt ?? '',
                });
              } else {
                reject(new Error('No download URL in response'));
              }
            },
            error: (error) => {
              console.error('Error fetching databank download URL:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error fetching databank download URL:', error);
      throw error;
    }
  }

  /**
   * Download databank as ZIP file (streams blob directly).
   * @param databankId The databank ID to download
   * @returns Promise with Blob of the ZIP file
   */
  async downloadZip(databankId: string): Promise<Blob> {
    try {
      const url = `${this.fileBaseUrl}v1/databanks/${databankId}/download`;

      console.log('Downloading databank as ZIP:', { url, databankId });

      return new Promise((resolve, reject) => {
        this.http
          .get(url, {
            responseType: 'blob',
            observe: 'response' as const,
          })
          .subscribe({
            next: (response) => {
              if (response.body) {
                resolve(response.body);
              } else {
                reject(new Error('No data received'));
              }
            },
            error: (error) => {
              console.error('Error downloading ZIP:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error downloading ZIP:', error);
      throw error;
    }
  }

  /**
   * Download assets
   * @param requestBody The request body containing asset IDs or download parameters
   * @returns Promise with download response
   */
  async downloadAssets(requestBody: any): Promise<any> {
    try {
      // Construct the full URL for asset download
      // URL: <basepathUrl>/v1/assets/download
      const url = `${this.fileBaseUrl}v1/assets/download`;

      // Prepare headers
      const headers = {
        'Content-Type': 'application/json',
      };

      // Make the API call using NetworkService
      const response = await this.networkService.post_api(
        url,
        requestBody,
        headers,
        true, // isToasterRequired - set to true to show error toasters
        true, // isLoaderRequired - set to true to show loader
      );

      return response;
    } catch (error) {
      console.error('Error downloading assets:', error);
      throw error;
    }
  }

  /**
   * Cancel an upload
   * @param databankId The databank ID
   * @param uploadId The upload ID to cancel
   * @param key The file key/name
   * @returns Promise with cancel response
   */
  async cancelUpload(
    databankId: string,
    uploadId: string,
    key: string
  ): Promise<{ data: any; status: number }> {
    try {
      const url = `${this.fileBaseUrl}v1/databanks/${databankId}/uploads/${uploadId}/cancel`;

      const body = { key };

      console.log('Cancelling upload:', { url, body });

      return new Promise((resolve, reject) => {
        this.http
          .put(url, body, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
            observe: 'response' as const,
          })
          .subscribe({
            next: (response) => {
              resolve({
                data: response.body,
                status: response.status,
              });
            },
            error: (error) => {
              console.error('Error cancelling upload:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error cancelling upload:', error);
      throw error;
    }
  }

  /**
   * Process databank files (publish)
   * @param databankId The databank ID
   * @param type The processing type (e.g., 'all')
   * @returns Promise with process response
   */
  async processDatabank(
    databankId: string,
    type: string = 'all'
  ): Promise<{ data: any; status: number }> {
    try {
      const url = `${this.fileBaseUrl}v1/databanks/${databankId}/process`;

      const body = { type };

      console.log('Processing databank:', { url, body });

      return new Promise((resolve, reject) => {
        this.http
          .post(url, body, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
            observe: 'response' as const,
          })
          .subscribe({
            next: (response) => {
              resolve({
                data: response.body,
                status: response.status,
              });
            },
            error: (error) => {
              console.error('Error processing databank:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error processing databank:', error);
      throw error;
    }
  }

  /**
   * Delete a file from databank
   * @param databankId The databank ID
   * @param key The file key/name to delete
   * @returns Promise with delete response
   */
  async deleteFile(
    databankId: string,
    key: string
  ): Promise<{ data: any; status: number }> {
    try {
      const url = `${this.fileBaseUrl}v1/databanks/${databankId}/files/delete`;

      const body = { key };

      console.log('Deleting file:', { url, body });

      return new Promise((resolve, reject) => {
        this.http
          .post(url, body, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
            observe: 'response' as const,
          })
          .subscribe({
            next: (response) => {
              resolve({
                data: response.body,
                status: response.status,
              });
            },
            error: (error) => {
              console.error('Error deleting file:', error);
              reject(error);
            },
          });
      });
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }
}

